import Spinner from "components/Spinner";
import StaticAutoComplete from "components/StaticAutoComplete";
import { setNotification } from "features/Notification/notificationSlice";
import { useState } from "react";
import { Button, Form, Modal } from "react-bootstrap";
import { useDispatch } from "react-redux";
import authHeader from "services/auth-header";
import API from "services/axios";
import timezones from "timezones-list";
import AttHolidaysList from "./AttHolidaysList";

export default function AttDeviceSummary({
  selectedDevice,
  setSelectedDevice,
  usersSummary,
  setUsersSummary,
  getDeviceDetails,
  setAllAttendanceUsers,
  calculateUsersSummary,
  usersLastSync,
  setUsersLastSync,
  logsLastSync,
  setLogsLastSync,
}) {
  /*   All States
   ********************************************* */
  const dispatch = useDispatch();

  const [loading, setLoading] = useState(false);
  const [editDeviceModal, setEditDeviceModal] = useState(false);
  const [formData, setFormData] = useState({
    name: "",
    ipAddress: "",
    port: "",
    relaxationMinutes: 0,
    overtimeAfterMinutes: 0,
  });

  /*   All Functions
   ********************************************* */
  const loadingOn = () => {
    setLoading(true);
  };

  const loadingOff = () => {
    setLoading(false);
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData({ ...formData, [name]: value });
  };

  const openEditModal = () => {
    setFormData({
      name: selectedDevice.name || "",
      ipAddress: selectedDevice.ipAddress || "",
      port: selectedDevice.port || "",
      relaxationMinutes: selectedDevice.relaxationMinutes || 0,
      overtimeAfterMinutes: selectedDevice.overtimeAfterMinutes || 0,
      timezone: selectedDevice.timezone || "",
    });
    setEditDeviceModal(true);
  };

  const updateDevice = async (e) => {
    e.preventDefault();
    try {
      loadingOn();
      const { data } = await API.patch(
        "/patch-attendance-devices/" + selectedDevice.id,
        {
          ...formData,
        },
        { headers: authHeader() }
      );
      if (data?.success) {
        await getDeviceDetails(selectedDevice.id);
        setEditDeviceModal(false);
        dispatch(
          setNotification({
            message: "Device updated successfully",
            type: "success",
          })
        );
      } else {
        dispatch(
          setNotification({
            message: data?.message || "Failed to update device",
            type: "error",
          })
        );
      }

      loadingOff();
    } catch (err) {
      loadingOff();
      dispatch(
        setNotification({
          message: err.message,
          type: "error",
        })
      );
    }
  };

  // Sync-Database
  const syncUsers = async (id) => {
    loadingOn();
    try {
      const { data } = await API.get("/device-users-sync/" + id, {
        headers: authHeader(),
      });

      if (data?.success) {
        // setUsersLastSync(data?.lastUsersSync);
        await getDeviceDetails(selectedDevice?.id);

        setAllAttendanceUsers(data?.data);
        calculateUsersSummary(data?.data);
        dispatch(
          setNotification({
            message: "Users synced successfully",
            type: "success",
          })
        );
      }
      if (!data?.success) {
        dispatch(
          setNotification({
            message: data.message,
            type: "error",
          })
        );
      }
      loadingOff();
    } catch (err) {
      loadingOff();
      dispatch(
        setNotification({
          message: err.message,
          type: "error",
        })
      );
    } finally {
      loadingOff();
    }
  };

  const syncWithDatabaseLogs = async (deviceId) => {
    try {
      loadingOn();
      const { data } = await API.get("/sync-database-logs/" + deviceId, {
        headers: authHeader(),
      });
      if (data?.success) {
        // setLogsLastSync(data?.lastLogsSync);
        await getDeviceDetails(selectedDevice?.id);
        dispatch(
          setNotification({
            message: "Device logs synced successfully",
            type: "success",
          })
        );
      } else {
        dispatch(
          setNotification({
            message: data?.message,
            type: "error",
          })
        );
      }

      loadingOff();
    } catch (err) {
      loadingOff();
      dispatch(
        setNotification({
          message: err.message,
          type: "error",
        })
      );
    }
  };

  /*   All UseEffects
   ********************************************* */

  return (
    <div>
      {loading && <Spinner />}
      {selectedDevice && (
        <div className="row">
          <div className="col-md-7 mb-4">
            {/* Device Details */}
            <div className="card " style={{}}>
              <div className="card-header py-1 bg-primary text-white d-flex justify-content-between align-items-center">
                <h5 className="mb-0">Device Details</h5>
                <button
                  className="btn btn-sm btn-light"
                  onClick={openEditModal}
                >
                  Edit Device
                </button>
              </div>
              <div className="card-body py-4">
                <table className="table mb-0 table-bordered">
                  <tbody>
                    <tr>
                      <th width="40%">Name</th>
                      <td>{selectedDevice.name}</td>
                    </tr>
                    <tr>
                      <th>IP Address : Port - Timezone</th>
                      <td>
                        {selectedDevice.ipAddress} : {selectedDevice.port} -{" "}
                        {selectedDevice.timezone}
                      </td>
                    </tr>

                    <tr>
                      <th>Company ID</th>
                      <td>{selectedDevice.companyId}</td>
                    </tr>
                    <tr>
                      <th>Relaxation Minutes : Overtime After Minutes</th>
                      <td>
                        {selectedDevice.relaxationMinutes}m :{" "}
                        {selectedDevice.overtimeAfterMinutes}m
                      </td>
                    </tr>
                    <tr className="align-middle">
                      <th>Actions</th>
                      <td className="py-1 ">
                        <button
                          onClick={() => {
                            if (!selectedDevice?.id) {
                              return;
                            }
                            syncUsers(selectedDevice?.id);
                          }}
                          className="btn btn-sm btn-info me-2"
                        >
                          Sync Users
                        </button>
                        <button
                          onClick={() => {
                            if (!selectedDevice?.id) {
                              return;
                            }
                            syncWithDatabaseLogs(selectedDevice?.id);
                            let tid = setInterval(() => {
                              syncWithDatabaseLogs(selectedDevice?.id);
                              clearInterval(tid);
                            }, 100);
                          }}
                          className="btn btn-sm btn-primary"
                        >
                          Sync Device Logs
                        </button>
                      </td>
                    </tr>
                  </tbody>
                </table>
              </div>
            </div>{" "}
            {/* Users Summary */}
            <div className="card " style={{}}>
              <div className="card-header py-1 bg-primary text-white">
                <h5 className="">Users Summary</h5>
              </div>
              <div className="card-body py-4">
                <div className="row text-center">
                  <div className="col-md-4">
                    <div className="p-3 border rounded bg-light">
                      <h3>{usersSummary.total}</h3>
                      <p className="mb-0">Total Users</p>
                    </div>
                  </div>
                  <div className="col-md-4">
                    <div className="p-3 border rounded bg-light">
                      <h3>{usersSummary.admin}</h3>
                      <p className="mb-0">Admins</p>
                    </div>
                  </div>
                  <div className="col-md-4">
                    <div className="p-3 border rounded bg-light">
                      <h3>{usersSummary.user}</h3>
                      <p className="mb-0">Regular Users</p>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
          {/* Holidays Section */}
          <div className="col-md-5 ">
            <AttHolidaysList selectedDevice={selectedDevice} />
          </div>
        </div>
      )}
      <Modal
        show={editDeviceModal}
        onHide={() => setEditDeviceModal(false)}
        contentClassName="bg-transparent"
        centered
      >
        <Modal.Body className="bg-white px-3 custom-border-radius">
          <Modal.Header className="py-0 ps-0 mb-3" closeButton>
            <Modal.Title>Edit Device</Modal.Title>
          </Modal.Header>
          <Form onSubmit={updateDevice}>
            <Form.Group className="mb-2">
              <Form.Label>Device Name</Form.Label>
              <Form.Control
                type="text"
                name="name"
                placeholder="Enter Device Name"
                autoFocus
                value={formData.name}
                onChange={handleChange}
                required
              />
            </Form.Group>

            <Form.Group className="mb-2">
              <Form.Label>IP Address</Form.Label>
              <Form.Control
                type="text"
                name="ipAddress"
                placeholder="Enter IP Address"
                value={formData.ipAddress}
                onChange={handleChange}
                required
              />
            </Form.Group>

            <Form.Group className="mb-2">
              <Form.Label>Port</Form.Label>
              <Form.Control
                type="text"
                name="port"
                placeholder="Enter Port"
                value={formData.port}
                onChange={handleChange}
                required
              />
            </Form.Group>
            <Form.Group className="mb-2">
              <Form.Label>Time Zone</Form.Label>
              <StaticAutoComplete
                dataList={timezones}
                fieldName={"tzCode"}
                type={"text"}
                name="timezone"
                value={formData?.timezone || ""}
                handleChange={(fieldName, value) => {
                  setFormData((prev) => {
                    return { ...prev, timezone: value };
                  });
                }}
                resultsKey={"label"}
                searchKeys={["tzCode", "label"]}
                classNames={"form-control "}
                suggestionsWidth={430}
                placeholder="Select Time Zone"
              />
            </Form.Group>

            <Form.Group className="mb-2">
              <Form.Label>Relaxation Minutes</Form.Label>
              <Form.Control
                type="number"
                name="relaxationMinutes"
                placeholder="15"
                value={formData.relaxationMinutes}
                onChange={handleChange}
                required
              />
            </Form.Group>

            <Form.Group className="mb-2">
              <Form.Label>Overtime After Minutes</Form.Label>
              <Form.Control
                type="number"
                name="overtimeAfterMinutes"
                placeholder="15"
                value={formData.overtimeAfterMinutes}
                onChange={handleChange}
                required
              />
            </Form.Group>

            <div className="d-flex justify-content-end">
              <Button
                variant="secondary"
                className="me-2"
                onClick={() => setEditDeviceModal(false)}
              >
                Cancel
              </Button>
              <Button variant="primary" type="submit">
                Update Device
              </Button>
            </div>
          </Form>
        </Modal.Body>
      </Modal>
    </div>
  );
}
